// StaffCardCaptureScreen.jsx
// =====================================================================
// v2-only step inserted between SignInScreen and ActiveSessionScreen.
// The pilot has typed their CathayID; we now ask them to take a photo
// of their CX Staff Card via the iPad's rear camera. On confirm:
//   1. submit /sessions/signin (same as v1)
//   2. upload the photo to /sessions/:client_uuid/staff-card
//   3. transition to ActiveSessionScreen
// If step 2 fails after step 1 succeeds we keep the session open and
// let the pilot retry — the session row exists either way, but the
// admin's "missing staff card" view (future feature) will surface it.
// =====================================================================

import { useRef, useState } from 'react';
import Header from '../components/Header.jsx';
import { api } from '../lib/api.js';
import { setActiveSession } from '../lib/storage.js';

// Lightweight uuid v4 — mirrors SignInScreen's helper to avoid an extra dep.
function genUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  a[6] = (a[6] & 0x0f) | 0x40;
  a[8] = (a[8] & 0x3f) | 0x80;
  const h = [...a].map((b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
}

export default function StaffCardCaptureScreen({
  device,
  cathayId,
  onSignedIn,
  onOverlap,
  onCancel,
}) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'signing_in' | 'uploading'

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setErr(null);
  };

  const openCamera = () => {
    setErr(null);
    fileRef.current?.click();
  };

  const submit = async () => {
    if (!file) {
      setErr('Take a photo of your CX Staff Card first');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Step 1 — sign in.
      setPhase('signing_in');
      const client_uuid = genUuid();
      const signin_at_utc = new Date().toISOString();
      let session;
      try {
        const r = await api.signin({ client_uuid, cathay_id: cathayId, signin_at_utc });
        session = r.session;
      } catch (e) {
        if (e.status === 409 && e.data?.code === 'overlap') {
          onOverlap({ cathayId, existing: e.data.existing });
          return;
        }
        if (e.data?.code === 'clock_skew') {
          setErr('iPad clock is out of sync. Ask your admin to check the date/time.');
          return;
        }
        setErr(e.message || 'Sign-in failed');
        return;
      }
      setActiveSession(session);

      // Step 2 — upload the photo. Session is already open; failure
      // here is non-fatal but surfaced so the pilot can retake/retry.
      setPhase('uploading');
      try {
        await api.uploadStaffCard(session.client_uuid, file);
      } catch (e) {
        setErr(
          `Sign-in succeeded but the photo upload failed: ${e.message || 'unknown error'}. ` +
          'Tap "Continue" to start your session anyway — an admin can re-attach the photo later.'
        );
        // Stash a hint so the active-session screen knows we should
        // offer a retry. (Future work: actually wire that retry up.)
        return;
      }

      onSignedIn(session);
    } finally {
      setBusy(false);
      setPhase('idle');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <Header deviceName={device?.name} />
      <main className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl p-8 space-y-6">
          <header className="text-center space-y-1">
            <h1 className="text-kiosk-lg font-semibold text-cathay-700">
              Photo of CX Staff Card
            </h1>
            <p className="text-kiosk text-slate-600">
              Hold your CX Staff Card flat on the table and tap below to capture.
            </p>
            <p className="text-sm text-slate-500">
              Signing in as <span className="font-mono text-slate-700">{cathayId}</span>
            </p>
          </header>

          {/* Hidden file input — opens the iPad's rear camera. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onPick}
          />

          {previewUrl ? (
            <div className="bg-slate-100 rounded-2xl overflow-hidden border border-slate-200">
              <img
                src={previewUrl}
                alt="Staff Card preview"
                className="w-full h-auto max-h-[40vh] object-contain bg-black/5"
              />
              <div className="p-3 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={openCamera}
                  disabled={busy}
                  className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-kiosk font-semibold py-3 rounded-xl"
                >
                  Retake
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white text-kiosk font-semibold py-3 rounded-xl shadow"
                >
                  {phase === 'signing_in' ? 'Signing in…' :
                   phase === 'uploading' ? 'Uploading…' :
                   'Confirm & sign in'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={openCamera}
              className="w-full bg-cathay-500 hover:bg-cathay-600 active:bg-cathay-700 text-white text-kiosk-lg font-semibold py-8 rounded-2xl shadow"
            >
              Open camera
            </button>
          )}

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-kiosk">
              {err}
            </div>
          )}

          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="w-full bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-kiosk font-semibold py-4 rounded-2xl"
          >
            Cancel
          </button>
        </div>
      </main>
    </div>
  );
}
